'use client';

import { useCallback } from 'react';
import { useCreateCommentMutation } from '@frameio/comments/hooks/gql/mutations/generated/CreateComment';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import {
  useSetCommentCreatedFromMarkerMutation,
  useSetFrameCommentIdsOnMarkersMutation,
} from '@frameio/shared/integrations/gql';
import { sleep } from '@frameio/utils';
import { usePluginFeatures } from '../usePluginFeatures';
import type { CommentFromMarkerPayload } from './utils';
import { stampMarkerExportCommentBody } from './utils';

/** Pace between sequential marker-export `createComment` calls (ms); avoids
 * bursting the API. */
const CREATE_COMMENT_FROM_MARKER_INTERVAL_MS = 50;

type CreateCommentFn = ReturnType<typeof useCreateCommentMutation>[0];
type SetFrameCommentIdsOnMarkersFn = ReturnType<
  typeof useSetFrameCommentIdsOnMarkersMutation
>[0];
type SetCommentCreatedFromMarkerFn = ReturnType<
  typeof useSetCommentCreatedFromMarkerMutation
>[0];

export type CreateFrameCommentsFromMarkersOptions = {
  documentId?: string | null;
};

type MarkerCommentLink = {
  frameCommentId: string;
  markerId: string;
};

type CreateCommentsFromMarkersResult = {
  commentIds: string[];
  failures: Error[];
};

type CreateCommentsFromMarkersBaseOptions = {
  assetId: string;
  createComment: CreateCommentFn;
  payloads: CommentFromMarkerPayload[];
};

const asError = (e: unknown) => (e instanceof Error ? e : new Error(String(e)));

async function createCommentFromMarkerPayload(options: {
  assetId: string;
  createComment: CreateCommentFn;
  payload: CommentFromMarkerPayload;
  text: string;
}): Promise<{ commentId?: string; error?: Error }> {
  const { assetId, createComment, payload, text } = options;
  const { duration, timestampMicroseconds } = payload;

  try {
    const mutationResult = await createComment({
      variables: {
        comment: {
          assetId,
          duration,
          text,
          timestampMicroseconds,
        },
      },
    });

    if (mutationResult.errors?.length) {
      return {
        error: new Error(
          mutationResult.errors.map((e) => e.message).join('; ')
        ),
      };
    }

    return {
      commentId: mutationResult.data?.createComment?.comment?.id ?? undefined,
    };
  } catch (e) {
    return { error: asError(e) };
  }
}

/** Pre-0.0.167 plugins: stamp user id in comment text for later sync exclusion. */
async function createFrameCommentsFromMarkersWithLegacyStamp(
  options: CreateCommentsFromMarkersBaseOptions & { userId: string }
): Promise<CreateCommentsFromMarkersResult> {
  const { assetId, createComment, payloads, userId } = options;
  const failures: Error[] = [];
  const commentIds: string[] = [];

  for (const [i, payload] of payloads.entries()) {
    if (i > 0) {
      // eslint-disable-next-line no-await-in-loop -- intentional pacing
      await sleep(CREATE_COMMENT_FROM_MARKER_INTERVAL_MS);
    }

    const { commentId, error } =
      // eslint-disable-next-line no-await-in-loop -- intentional pacing
      await createCommentFromMarkerPayload({
        assetId,
        createComment,
        payload,
        text: stampMarkerExportCommentBody(payload.initialText, userId),
      });

    if (error) {
      failures.push(error);
    } else if (!commentId) {
      failures.push(new Error('createComment returned no comment id'));
    } else {
      commentIds.push(commentId);
    }
  }

  return { commentIds, failures };
}

/**
 * Plugin ≥ 0.0.167: track upload comments on the host and set frameCommentId on markers.
 *
 * Two host mutations, not one, because they have different urgency/cost:
 * `setCommentCreatedFromMarker` is a cheap per-comment registry write, sent immediately after
 * each comment is created so the host's `sendMarkers` can exclude it right away — otherwise a
 * comment created early in a multi-marker export could round-trip back as a duplicate marker
 * before the batch link call below ever runs. `setFrameCommentIdsOnMarkers` also writes marker
 * Properties on the host document (on Premiere, one `executeTransaction` covering every link),
 * so it's deliberately deferred and batched once at the end instead of per comment — doing that
 * per comment would mean one undo-history entry and one full sequence marker scan per marker.
 */
async function createFrameCommentsFromMarkersWithHostLinks(
  options: CreateCommentsFromMarkersBaseOptions & {
    documentId: string;
    setCommentCreatedFromMarker: SetCommentCreatedFromMarkerFn;
    setFrameCommentIdsOnMarkers: SetFrameCommentIdsOnMarkersFn;
  }
): Promise<CreateCommentsFromMarkersResult> {
  const {
    assetId,
    createComment,
    documentId,
    payloads,
    setCommentCreatedFromMarker,
    setFrameCommentIdsOnMarkers,
  } = options;
  const failures: Error[] = [];
  const commentIds: string[] = [];
  const pendingLinks: MarkerCommentLink[] = [];

  for (const [i, payload] of payloads.entries()) {
    if (i > 0) {
      // eslint-disable-next-line no-await-in-loop -- intentional pacing
      await sleep(CREATE_COMMENT_FROM_MARKER_INTERVAL_MS);
    }

    const { commentId, error } =
      // eslint-disable-next-line no-await-in-loop -- intentional pacing
      await createCommentFromMarkerPayload({
        assetId,
        createComment,
        payload,
        text: payload.initialText,
      });

    if (error) {
      failures.push(error);
    } else if (!commentId) {
      failures.push(new Error('createComment returned no comment id'));
    } else {
      commentIds.push(commentId);

      let trackingSucceeded = false;
      try {
        // Sent per comment, not batched with the link call below — see function doc.
        const trackResult =
          // eslint-disable-next-line no-await-in-loop -- intentional pacing
          await setCommentCreatedFromMarker({
            variables: {
              input: {
                assetId,
                commentId,
              },
            },
          });

        trackingSucceeded =
          !trackResult.errors?.length &&
          Boolean(trackResult.data?.setCommentCreatedFromMarker);

        if (!trackingSucceeded) {
          failures.push(
            new Error(
              trackResult.errors?.map((e) => e.message).join('; ') ||
                `Failed to track comment created from marker ${commentId}`
            )
          );
        }
      } catch (e) {
        failures.push(asError(e));
      }

      // Skip the link when tracking failed: otherwise the marker would get
      // `frameCommentId` set for a comment the upload registry doesn't know
      // about, so a future export would re-create a comment for it (the
      // registry lookup that skips already-uploaded markers relies on the
      // comment id being registered, not just linked).
      if (trackingSucceeded && payload.markerId) {
        pendingLinks.push({
          frameCommentId: commentId,
          markerId: payload.markerId,
        });
      } else if (!trackingSucceeded && payload.markerId) {
        failures.push(
          new Error(
            `Comment ${commentId} created but not registered; skipping marker link for marker ${payload.markerId}`
          )
        );
      }
    }
  }

  if (pendingLinks.length === 0) {
    return { commentIds, failures };
  }

  try {
    // Batched once for every marker in this export — see function doc.
    const linkResult = await setFrameCommentIdsOnMarkers({
      variables: {
        input: {
          assetId,
          documentId,
          links: pendingLinks,
        },
      },
    });

    if (
      linkResult.errors?.length ||
      !linkResult.data?.setFrameCommentIdsOnMarkers
    ) {
      failures.push(
        new Error(
          linkResult.errors?.map((e) => e.message).join('; ') ||
            `Failed to set frame comment ids on ${pendingLinks.length} marker(s)`
        )
      );
    }
  } catch (e) {
    failures.push(asError(e));
  }

  return { commentIds, failures };
}

/**
 * Hook that returns a function to create Frame comments from prepared payloads
 * for `assetId`. When supported by the host plugin, links created comments back
 * to source host markers instead of stamping user id in comment text.
 * Callers typically build payloads via `useExportMarkersAsComments` rather
 * than calling this hook directly.
 */
export function useCreateFrameCommentsFromMarkers() {
  const [createComment] = useCreateCommentMutation();
  const [setFrameCommentIdsOnMarkers] = useSetFrameCommentIdsOnMarkersMutation({
    context: { resolveWithUXP: true },
  });
  const [setCommentCreatedFromMarker] = useSetCommentCreatedFromMarkerMutation({
    context: { resolveWithUXP: true },
  });
  const userId = useGetActiveUserId();
  const { canUseHiddenMarkerUploadIds } = usePluginFeatures();

  return useCallback(
    async (
      assetId: string,
      payloads: CommentFromMarkerPayload[],
      options?: CreateFrameCommentsFromMarkersOptions
    ): Promise<CreateCommentsFromMarkersResult> => {
      const baseOptions = { assetId, createComment, payloads };

      if (canUseHiddenMarkerUploadIds && options?.documentId) {
        return createFrameCommentsFromMarkersWithHostLinks({
          ...baseOptions,
          documentId: options.documentId,
          setCommentCreatedFromMarker,
          setFrameCommentIdsOnMarkers,
        });
      }

      return createFrameCommentsFromMarkersWithLegacyStamp({
        ...baseOptions,
        userId,
      });
    },
    [
      canUseHiddenMarkerUploadIds,
      createComment,
      setCommentCreatedFromMarker,
      setFrameCommentIdsOnMarkers,
      userId,
    ]
  );
}
